// =============================================================================
// FILE PATH : src/modules/pms/task_template/components/TaskTemplateFormDrawer.js
// MODULE    : PMS → Task Template
// PURPOSE   : Drawer form for Create/Edit Template with Maximize/Restore
// =============================================================================

import React, { useEffect, useState } from "react";
import { TEMPLATE_TYPES, DEFAULT_TASK_ITEM } from "../constants/taskTemplateConstants";
import TaskItemGrid from "./TaskItemGrid";
import { saveTemplate } from "../services/taskTemplateService";


const overlayStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: "rgba(0,0,0,0.3)",
  zIndex: 1000
};

const drawerBase = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100%",
  background: "#fff",
  zIndex: 1001,
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease"
};

const headerStyle = {
  padding: "16px",
  borderBottom: "1px solid #eee",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const bodyStyle = {
  padding: "16px",
  overflowY: "auto",
  flex: 1
};

const footerStyle = {
  padding: "12px 16px",
  borderTop: "1px solid #eee",
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px"
};

const inputStyle = {
  width: "100%",
  padding: "8px",
  marginBottom: "12px",
  borderRadius: "6px",
  border: "1px solid #ccc"
};


const TaskTemplateFormDrawer = ({
  open,
  onClose,
  onSuccess,
  editData = null,
  equipmentList = []
}) => {

  const [isMaximized, setIsMaximized] = useState(false);

  const [form, setForm] = useState({
    template_name: "",
    template_type: "PROJECT",
    equipment_id: ""
  });

  const [items, setItems] = useState([DEFAULT_TASK_ITEM]);

  // ---------------------------------------------------------------------------
  // LOAD EDIT DATA
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (editData) {
      setForm({
        id: editData.id,
        template_name: editData.template_name,
        template_type: editData.template_type,
        equipment_id: editData.equipment_id || ""
      });

      setItems(editData.items || []);
    } else {
      resetForm();
    }
  }, [editData]);

  const resetForm = () => {
    setForm({
      template_name: "",
      template_type: "PROJECT",
      equipment_id: ""
    });
    setItems([DEFAULT_TASK_ITEM]);
  };

  // ---------------------------------------------------------------------------
  // HANDLE CHANGE
  // ---------------------------------------------------------------------------
  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  // ---------------------------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------------------------
  const handleSave = async () => {

    if (!form.template_name) {
      alert("Template name is required");
      return;
    }

    if (form.template_type === "EQUIPMENT" && !form.equipment_id) {
      alert("Equipment is required");
      return;
    }

    if (!items.length) {
      alert("At least one task is required");
      return;
    }

    const payload = {
    ...form,
    equipment_id:
        form.template_type === "EQUIPMENT"
        ? form.equipment_id
        : null,
    items
    };

    try {
      await saveTemplate(payload);
      onSuccess();
      onClose();
    } catch (err) {
      alert("Error saving template");
    }
  };

  if (!open) return null;

  // ---------------------------------------------------------------------------
  // DRAWER STYLE SWITCH
  // ---------------------------------------------------------------------------
  const drawerStyle = isMaximized
    ? {
        ...drawerBase,
        width: "100%",
        left: 0
      }
    : {
        ...drawerBase,
        width: "600px"
      };

  return (
    <>
      <div style={overlayStyle} onClick={onClose}></div>

      <div style={drawerStyle}>

        {/* HEADER */}
        <div style={headerStyle}>
          <div>
            {editData ? "Edit Template" : "Create Template"}
          </div>

          <div>
            <button onClick={() => setIsMaximized(!isMaximized)}>
              {isMaximized ? "🗗" : "⛶"}
            </button>
            <button onClick={onClose} style={{ marginLeft: "10px" }}>
              ✖
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={bodyStyle}>

          {/* TEMPLATE NAME */}
          <input
            type="text"
            placeholder="Template Name"
            value={form.template_name}
            onChange={(e) => handleChange("template_name", e.target.value)}
            style={inputStyle}
          />

          {/* TEMPLATE TYPE */}
          <select
            value={form.template_type}
            onChange={(e) => handleChange("template_type", e.target.value)}
            style={inputStyle}
          >
            {TEMPLATE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          {/* EQUIPMENT (ONLY IF EQUIPMENT TYPE) */}
          {form.template_type === "EQUIPMENT" && (
            <select
              value={form.equipment_id}
              onChange={(e) => handleChange("equipment_id", e.target.value)}
              style={inputStyle}
            >
              <option value="">Select Equipment</option>
              {equipmentList.map((eq) => (
                <option key={eq.id} value={eq.id}>
                    {eq.name}
                </option>
                ))}
            </select>
          )}

          {/* TASK GRID */}
          <TaskItemGrid items={items} setItems={setItems} />

        </div>

        {/* FOOTER */}
        <div style={footerStyle}>
          <button onClick={onClose}>Cancel</button>
          <button onClick={handleSave} style={{ background: "#1976d2", color: "#fff", padding: "8px 14px", border: "none", borderRadius: "6px" }}>
            Save
          </button>
        </div>

      </div>
    </>
  );
};

export default TaskTemplateFormDrawer;